/**
 * Pure H OS · Phase D · P0-3 · Scorecards
 *
 * Executive scorecards for CEO, CFO, CMO, COO, Chief Perfumer, and
 * Chief Growth. Derived purely from existing live collections — no
 * new backend collection, no new API endpoint. Each function points
 * at the ONE overview singleton (or derived aggregate) that already
 * lives in MongoDB.
 *
 * Reuses ONLY existing Pure H primitives: Card, SectionHeader,
 * SeverityBadge, DeltaChip, TabBar, Drawer, Button.
 */
import { useMemo, useState } from "react";
import { Card, SectionHeader, SeverityBadge, DeltaChip } from "../components/lib";
import { DocumentActions } from "../components/DocumentActions";
import { TabBar, Drawer } from "../components/lib.overlay";
import { EXECUTIVE_DASHBOARD_DATA }           from "../mocks/data";
import { FINANCE_OVERVIEW }                    from "../mocks/financeData";
import { MARKETING_OVERVIEW }                  from "../mocks/marketingData";
import { OPERATIONS_OVERVIEW }                 from "../mocks/operationsData";
import { GROWTH_KPIS }                         from "../mocks/growthData";
import { RND_PRODUCTS, FORMULAS, BLIND_PANELS, RAW_MATERIALS } from "../mocks/rndData";
import { DECISIONS }                           from "../mocks/decisionsData";
import { useNavigate }                         from "react-router-dom";

// ── Types (derived, not persisted) ──────────────────────────────
type Trend = "up" | "down" | "flat";
type Kpi = { key: string; label: string; value: string; delta: number; trend: Trend; hint?: string };

type Scorecard = {
  key: string;                // "ceo" | "cfo" | …
  function: string;           // "CEO" | "CFO" | …
  owner: string;              // e.g. "Hassan (Owner)"
  seat: string;               // e.g. "Founder · Owner"
  headline: string;           // one-line narrative
  kpis: Kpi[];                // 4–6 items
  linkedModules: { label: string; href: string }[];
};

// ── Derivation helpers ─────────────────────────────────────────
function avgTrend(kpis: Kpi[]): Trend {
  if (!kpis.length) return "flat";
  const avg = kpis.reduce((a, k) => a + (k.delta ?? 0), 0) / kpis.length;
  if (avg >  2) return "up";
  if (avg < -2) return "down";
  return "flat";
}

function statusFor(trend: Trend): "high" | "medium" | "low" {
  return trend === "up" ? "low" : trend === "down" ? "high" : "medium";
}

// Chief Perfumer KPIs are derived from R&D collections (no dedicated
// KPI singleton exists yet — kept computed here, no new fixture).
function perfumerKpis(): Kpi[] {
  const pipeline = RND_PRODUCTS.filter((p) => p.phase !== "launched").length;
  const approvedFormulas = FORMULAS.filter((f) => f.status === "approved").length;
  const advances = BLIND_PANELS.filter((b) => b.recommendation === "advance").length;
  const passRate = BLIND_PANELS.length > 0
    ? Math.round((advances / BLIND_PANELS.length) * 100)
    : 0;
  const avgScore = BLIND_PANELS.length > 0
    ? (BLIND_PANELS.reduce((a, b) => a + b.overall, 0) / BLIND_PANELS.length)
    : 0;
  const materialsHealthy = RAW_MATERIALS.filter((m) => m.onHandKg > 20).length;
  const rndDecisions = DECISIONS.filter((d) => d.module === "R&D").length;
  return [
    { key: "pipeline", label: "Products in pipeline", value: String(pipeline),
      delta: 0, trend: "flat", hint: `of ${RND_PRODUCTS.length} total` },
    { key: "approved-formulas", label: "Approved formulas", value: String(approvedFormulas),
      delta: 2, trend: "up" },
    { key: "panel-pass-rate", label: "Blind panel pass rate", value: `${passRate}%`,
      delta: 5, trend: "up" },
    { key: "avg-panel-score", label: "Avg panel score", value: avgScore.toFixed(1),
      delta: 0.4, trend: "up", hint: "of 10" },
    { key: "raw-materials-healthy", label: "Raw materials healthy", value: `${materialsHealthy}/${RAW_MATERIALS.length}`,
      delta: 0, trend: "flat" },
    { key: "rd-decisions", label: "R&D decisions logged", value: String(rndDecisions),
      delta: 1, trend: "up", hint: "last 90 d" },
  ];
}

// ── Scorecard registry ─────────────────────────────────────────
function buildScorecards(): Scorecard[] {
  const ceoKpis     = (EXECUTIVE_DASHBOARD_DATA.kpis ?? []).slice(0, 6) as Kpi[];
  const cfoKpis     = (FINANCE_OVERVIEW.kpis        ?? []).slice(0, 6) as Kpi[];
  const cmoKpis     = (MARKETING_OVERVIEW.kpis      ?? []).slice(0, 6) as Kpi[];
  const cooKpis     = (OPERATIONS_OVERVIEW.kpis     ?? []).slice(0, 6) as Kpi[];
  const growthKpis  = (GROWTH_KPIS                  ?? []).slice(0, 6) as Kpi[];
  const perfumer    = perfumerKpis();

  return [
    {
      key: "ceo", function: "CEO", owner: "Hassan (Owner)", seat: "Founder · Owner",
      headline: "Set the direction. Approve capital. Close the loop.",
      kpis: ceoKpis,
      linkedModules: [
        { label: "Executive Dashboard", href: "/pureh/dashboard" },
        { label: "Decisions",           href: "/pureh/decisions" },
        { label: "Reports",             href: "/pureh/reports" },
      ],
    },
    {
      key: "cfo", function: "CFO", owner: "Finance team", seat: "Chief Financial Officer",
      headline: "Cash, margin, runway. Zero surprises on the payment calendar.",
      kpis: cfoKpis,
      linkedModules: [
        { label: "Finance Overview",  href: "/pureh/finance/overview" },
        { label: "Cash Flow",         href: "/pureh/finance/cash" },
        { label: "Invoices",          href: "/pureh/finance/invoices" },
        { label: "Vendor Payments",   href: "/pureh/finance/vendors" },
      ],
    },
    {
      key: "cmo", function: "CMO", owner: "Kade · Chief Growth", seat: "Chief Marketing Officer",
      headline: "Grow reach. Ship on the calendar. Keep ROAS above 3×.",
      kpis: cmoKpis,
      linkedModules: [
        { label: "Marketing Overview", href: "/pureh/marketing/overview" },
        { label: "Calendar",           href: "/pureh/marketing/calendar" },
        { label: "Channels",           href: "/pureh/marketing/channels" },
        { label: "Creators",           href: "/pureh/marketing/creators" },
      ],
    },
    {
      key: "coo", function: "COO", owner: "Atlas · Chief Ops", seat: "Chief Operating Officer",
      headline: "Batches on time, QC pass, no supplier surprises.",
      kpis: cooKpis,
      linkedModules: [
        { label: "Operations Overview", href: "/pureh/operations/overview" },
        { label: "Batches",             href: "/pureh/operations/batches" },
        { label: "Quality",             href: "/pureh/operations/quality" },
        { label: "Suppliers",           href: "/pureh/operations/suppliers" },
      ],
    },
    {
      key: "chief-perfumer", function: "Chief Perfumer", owner: "Iris · Chief Perfumer",
      seat: "Master perfumer · R&D lead",
      headline: "Advance the pipeline. Pass panels. Protect the compendium.",
      kpis: perfumer,
      linkedModules: [
        { label: "R&D Library",     href: "/pureh/rd/library" },
        { label: "Formulas",        href: "/pureh/rd/formulas" },
        { label: "Blind Panels",    href: "/pureh/rd/blind" },
        { label: "Lab Sessions",    href: "/pureh/rd/lab" },
      ],
    },
    {
      key: "chief-growth", function: "Chief Growth", owner: "Kade · Chief Growth",
      seat: "Head of Growth",
      headline: "Retention, LTV, cohort health — compounding, not just spiking.",
      kpis: growthKpis,
      linkedModules: [
        { label: "Growth Overview", href: "/pureh/growth/overview" },
        { label: "Retention",       href: "/pureh/growth/retention" },
        { label: "Attribution",     href: "/pureh/growth/attribution" },
        { label: "Loyalty",         href: "/pureh/growth/loyalty" },
      ],
    },
  ];
}

// ── Page ───────────────────────────────────────────────────────
type Tab = "all" | Scorecard["key"];

export default function Scorecards() {
  const [tab, setTab] = useState<Tab>("all");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const navigate = useNavigate();

  const cards = useMemo(buildScorecards, []);
  const filtered = useMemo(
    () => (tab === "all" ? cards : cards.filter((c) => c.key === tab)),
    [cards, tab],
  );
  const current = openKey ? cards.find((c) => c.key === openKey) ?? null : null;

  return (
    <div className="space-y-6" data-testid="scorecards-page">
      <Card testId="sc-filters">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div />
          <DocumentActions docKey="scorecard-report" testId="sc-doc-actions" />
        </div>
        <TabBar<Tab>
          testId="sc-tabs"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "all",            label: `All ${cards.length}` },
            { key: "ceo",            label: "CEO" },
            { key: "cfo",            label: "CFO" },
            { key: "cmo",            label: "CMO" },
            { key: "coo",            label: "COO" },
            { key: "chief-perfumer", label: "Chief Perfumer" },
            { key: "chief-growth",   label: "Chief Growth" },
          ]}
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="sc-grid">
        {filtered.map((c) => {
          const trend = avgTrend(c.kpis);
          return (
            <Card key={c.key} testId={`sc-${c.key}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--ph-muted)" }}>
                    {c.function} · {c.seat}
                  </div>
                  <div className="mt-1 text-lg font-semibold" style={{ color: "var(--ph-text)" }}>
                    {c.owner}
                  </div>
                </div>
                <SeverityBadge severity={statusFor(trend)} />
              </div>

              <div className="text-xs mb-5 leading-relaxed" style={{ color: "var(--ph-muted)" }}>
                {c.headline}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {c.kpis.map((k) => (
                  <div key={k.key} data-testid={`sc-${c.key}-kpi-${k.key}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>{k.label}</div>
                    <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)", letterSpacing: "-0.01em" }}>
                      {k.value}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      {typeof k.delta === "number" && (k.trend === "up" || k.trend === "down" || k.trend === "flat")
                        ? <DeltaChip delta={k.delta} trend={k.trend} />
                        : null}
                      {k.hint && <span className="text-[10px]" style={{ color: "var(--ph-muted)" }}>{k.hint}</span>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 pt-4 flex items-center justify-between" style={{ borderTop: "1px solid var(--ph-border-subtle)" }}>
                <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>
                  {c.linkedModules.length} linked modules
                </div>
                <button
                  type="button"
                  data-testid={`sc-open-${c.key}`}
                  onClick={() => setOpenKey(c.key)}
                  className="text-xs underline underline-offset-4"
                  style={{ color: "var(--ph-text)" }}>
                  View detail
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      <Drawer open={!!current} onClose={() => setOpenKey(null)} testId="sc-drawer"
              title={current ? `${current.function} scorecard` : ""}>
        {current && (
          <div className="space-y-6">
            <SectionHeader eyebrow={current.function} title={current.owner} />
            {/* Single-record Scorecard PDF · ORVAN branded. Seed IDs are
                `sc-{key}` (see fixtures.json scorecards seed). */}
            <div className="pb-3 -mt-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
              <DocumentActions docKey="scorecard-report" recordId={`sc-${current.key}`} testId={`sc-single-${current.key}`} compact />
            </div>
            <div className="text-xs -mt-4" style={{ color: "var(--ph-muted)" }}>
              {current.seat}
            </div>

            <div className="text-sm leading-relaxed" style={{ color: "var(--ph-text)" }}>
              {current.headline}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                KPIs
              </div>
              <div className="grid grid-cols-2 gap-4">
                {current.kpis.map((k) => (
                  <div key={k.key}>
                    <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>{k.label}</div>
                    <div className="mt-1 text-base font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{k.value}</div>
                    <div className="mt-1"><DeltaChip delta={k.delta} trend={k.trend} /></div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] mb-2" style={{ color: "var(--ph-muted)" }}>
                Linked modules
              </div>
              <div className="flex flex-wrap gap-2">
                {current.linkedModules.map((m) => (
                  <button key={m.href}
                    type="button"
                    onClick={() => { setOpenKey(null); navigate(m.href); }}
                    className="px-3 py-1.5 text-xs rounded"
                    data-testid={`sc-link-${current.key}-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                    style={{
                      background: "var(--ph-surface-well)",
                      border: "1px solid var(--ph-border-subtle)",
                      color: "var(--ph-text)",
                    }}>
                    {m.label} →
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
